import React from 'react';
import { Link } from 'react-router-dom';
import { Home, ArrowRight, Compass, Trophy, Users, BookOpen, Mail } from 'lucide-react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { Button } from '../components/ui/button';

const quickLinks = [
  { title: "About STEM Education Trust", path: "/about", icon: Users, desc: "Empowering young roboticists across India" },
  { title: "BYKM Nationals 2026", path: "/fgc-nationals-2026", icon: Trophy, desc: "Explore the national robotics finals" },
  { title: "Team Performance History", path: "/performance", icon: Compass, desc: "Track Team India's global challenge record" },
  { title: "Blogs & Stories", path: "/blogs", icon: BookOpen, desc: "Read latest competition recaps and updates" },
  { title: "Contact Us", path: "/contact", icon: Mail, desc: "Reach out to STEM Education Trust team" }
];

const NotFoundPage = () => {
  return (
    <div className="min-h-screen bg-cream flex flex-col justify-between" data-testid="not-found-page">
      <Header />

      <main className="pt-28 pb-20 flex-grow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Main 404 Hero Card */}
          <div className="text-center max-w-3xl mx-auto py-12 px-6 sm:px-12 bg-cream-dark border border-pragmatic/30 rounded-3xl shadow-xl relative overflow-hidden mb-16">
            <div className="absolute inset-0 bg-gradient-to-br from-dwarfpony/5 via-transparent to-richolive/5 pointer-events-none" />
            
            {/* Styled 404 Number */}
            <div className="relative inline-block mb-4">
              <span className="text-8xl sm:text-9xl font-black tracking-tighter text-transparent bg-clip-text bg-gradient-to-r from-dwarfpony via-dwarfpony-dark to-midnight">
                404
              </span>
              <span className="absolute -top-2 -right-6 text-xs font-bold uppercase tracking-widest px-3 py-1 bg-dwarfpony text-white rounded-full shadow-md animate-bounce">
                Lost in Space
              </span>
            </div>

            <h1 className="text-3xl sm:text-4xl font-extrabold text-midnight mb-4">
              Oops! Page Not Found
            </h1>

            <p className="text-base sm:text-lg text-deepwalnut leading-relaxed max-w-xl mx-auto mb-8">
              The page you are looking for might have been moved, renamed, or doesn't exist. Let's get you back on track to explore Team India's STEM journey.
            </p>

            {/* Action Buttons */}
            <div className="flex flex-wrap items-center justify-center gap-4">
              <Link to="/">
                <Button className="bg-dwarfpony hover:bg-dwarfpony-dark text-white font-bold rounded-xl px-6 py-3 text-sm shadow-lg shadow-dwarfpony/25 flex items-center gap-2">
                  <Home size={18} />
                  <span>Return to Homepage</span>
                </Button>
              </Link>
              <Link to="/fgc-nationals-2026">
                <Button variant="outline" className="border-pragmatic/40 text-midnight hover:bg-cream text-sm font-bold rounded-xl px-6 py-3 flex items-center gap-2">
                  <span>Explore BYKM 2026</span>
                  <ArrowRight size={18} />
                </Button>
              </Link>
            </div>
          </div>

          {/* Quick Links Directory */}
          <div className="max-w-4xl mx-auto">
            <h2 className="text-xs font-extrabold uppercase tracking-widest text-heavyrain text-center mb-6">
              Popular Pages You Might Be Looking For
            </h2>

            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
              {quickLinks.map((item, idx) => {
                const IconComp = item.icon;
                return (
                  <Link key={idx} to={item.path} className="group">
                    <div className="bg-cream border border-pragmatic/30 hover:border-dwarfpony/40 rounded-2xl p-5 transition-all duration-300 shadow-sm hover:shadow-md h-full flex flex-col justify-between">
                      <div>
                        <div className="w-10 h-10 rounded-xl bg-dwarfpony/10 text-dwarfpony flex items-center justify-center mb-3 group-hover:bg-dwarfpony group-hover:text-white transition-colors">
                          <IconComp size={20} />
                        </div>
                        <h3 className="text-sm font-bold text-midnight mb-1 group-hover:text-dwarfpony transition-colors">
                          {item.title}
                        </h3>
                        <p className="text-xs text-heavyrain leading-relaxed">
                          {item.desc}
                        </p>
                      </div>
                      <div className="mt-4 flex items-center text-xs font-bold text-dwarfpony group-hover:translate-x-1 transition-transform">
                        <span>Visit Page</span>
                        <ArrowRight size={14} className="ml-1" />
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default NotFoundPage;
